import i18n from "../util/i18n.ts";

export type FieldTier = "primary" | "secondary" | "hidden";

export type DisplayField = {
  key: string;
  labelKey?: string;
  defaultLabel: string;
  tier: FieldTier;
  reason?: string;
};

export type MobileDisplayPolicy = {
  id: string;
  fields: readonly DisplayField[];
  primaryFields: readonly DisplayField[];
  secondaryFields: readonly DisplayField[];
  hiddenFields: readonly DisplayField[];
  primaryFieldKeys: readonly string[];
  secondaryFieldKeys: readonly string[];
  hiddenFieldKeys: readonly string[];
  maxPrimaryActions: 1;
  maxPrimaryFields: number;
};

export const mobileUnavailableLabel = "Unavailable";

const getMobileUnavailableLabel = () => {
  if (!i18n.isInitialized) {
    return mobileUnavailableLabel;
  }

  const translated = i18n.t("common.unavailable", {
    ns: "ui",
    defaultValue: mobileUnavailableLabel,
  });

  return typeof translated === "string" && translated.length > 0
    ? translated
    : mobileUnavailableLabel;
};

export const mobileDisplayLabelDefaults = {
  advanced: "Advanced",
  all: "All seasons",
  career: "Career",
  combined: "Combined",
  gameHighs: "Game highs",
  multiSeason: "Multi-season",
  opponent: "Opponent",
  opponentShotLocations: "Opponent shot locations",
  per36: "Per 36 minutes",
  perGame: "Per game",
  playoffs: "Playoffs",
  regularSeason: "Regular season",
  season: "Season",
  shotLocations: "Shot locations",
  team: "Team",
  teamShotLocations: "Team shot locations",
  totals: "Totals",
} as const;

export type MobileDisplayLabelToken = keyof typeof mobileDisplayLabelDefaults;

const getLocalizedMobileDisplayLabel = (token: MobileDisplayLabelToken) => {
  const defaultValue = mobileDisplayLabelDefaults[token];

  if (!i18n.isInitialized) {
    return defaultValue;
  }

  const translated = i18n.t(`mobileDisplay.${token}`, {
    ns: "ui",
    defaultValue,
  });

  return typeof translated === "string" && translated.length > 0 ? translated : defaultValue;
};

export const getMobileDisplayLabel = (value: unknown, fallback?: string) => {
  const fallbackLabel = fallback ?? getMobileUnavailableLabel();

  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }

  if (typeof value !== "string" || value.length === 0) {
    return fallbackLabel;
  }

  if (Object.hasOwn(mobileDisplayLabelDefaults, value)) {
    return getLocalizedMobileDisplayLabel(value as MobileDisplayLabelToken);
  }

  return fallbackLabel;
};

export const getMobileDisplayContextLabel = (
  values: readonly unknown[],
  fallback?: string,
) => values.map((value) => getMobileDisplayLabel(value, fallback)).join(" / ");

export type MobileVisibleTextHygieneViolation = {
  id: "unknownPlaceholder" | "rawEnumToken" | "qaCopy";
  text: string;
};

const rawVisibleEnumTokens = new Set([
  "advanced",
  "career",
  "multiSeason",
  "opponent",
  "opponentShotLocations",
  "per36",
  "perGame",
  "regularSeason",
  "season",
  "shotLocations",
  "team",
  "teamShotLocations",
]);

const visibleTextFromMarkup = (text: string) =>
  text
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export const getMobileVisibleTextHygieneViolations = (
  text: string,
): MobileVisibleTextHygieneViolation[] => {
  const visibleText = visibleTextFromMarkup(text);
  const violations: MobileVisibleTextHygieneViolation[] = [];

  if (visibleText.includes("???")) {
    violations.push({
      id: "unknownPlaceholder",
      text: "???",
    });
  }

  if (/\bQA\b/.test(visibleText)) {
    violations.push({
      id: "qaCopy",
      text: "QA",
    });
  }

  const segments = visibleText
    .split(/[\/|•,;()[\]{}]+/)
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0);

  for (const segment of segments) {
    if (rawVisibleEnumTokens.has(segment)) {
      violations.push({
        id: "rawEnumToken",
        text: segment,
      });
    }
  }

  return violations;
};

const field = (
  key: string,
  defaultLabel: string,
  tier: FieldTier,
  reason?: string,
  labelKey?: string,
): DisplayField => ({
  key,
  defaultLabel,
  tier,
  reason,
  labelKey,
});

export const rosterPlayerFields: DisplayField[] = [
  field("name", "Name", "primary"),
  field("positionAge", "Position/Age", "primary"),
  field("ovrPot", "Overall/Potential", "primary"),
  field("contract", "Contract", "primary"),
  field("role", "Role", "primary"),
  field("mood", "Mood", "secondary"),
  field("coreStats", "Core stats", "secondary"),
  field("injuryImpact", "Injury impact", "secondary"),
  field("country", "Country", "hidden", "Low-value bio detail for mobile cards."),
  field("acquired", "Acquired", "hidden", "Front-office history belongs in details."),
  field("yearsWithTeam", "Years with team", "hidden"),
  field("ratingsDetail", "Full ratings", "hidden"),
  field("fullStats", "Full stats", "hidden"),
];

export const marketPlayerFields: DisplayField[] = [
  field("askingPrice", "Asking price", "primary"),
  field("years", "Years", "primary"),
  field("affordability", "Affordability", "primary"),
  field("interest", "Interest", "primary"),
  field("ovrPot", "Overall/Potential", "primary"),
  field("positionAge", "Position/Age", "primary"),
  field("fitTags", "Fit tags", "secondary"),
  field("fullStats", "Full stats", "hidden"),
  field("acquired", "Acquired", "hidden"),
  field("moodDetail", "Detailed mood", "hidden"),
];

export const tradeAssetFields: DisplayField[] = [
  field("assetName", "Asset", "primary"),
  field("positionAge", "Position/Age", "primary"),
  field("ovrPot", "Overall/Potential", "primary"),
  field("contract", "Contract", "primary"),
  field("draftPickYearRound", "Pick year/round", "primary"),
  field("salaryImpact", "Salary impact", "secondary"),
  field("strengthImpact", "Strength impact", "secondary"),
  field("risk", "Risk", "secondary"),
  field("alternatives", "Alternatives", "secondary"),
  field("bulkExclude", "Bulk/exclude actions", "hidden"),
  field("fullStats", "Full stats", "hidden"),
];

export const draftProspectFields: DisplayField[] = [
  field("rank", "Rank", "primary"),
  field("name", "Name", "primary"),
  field("positionAge", "Position/Age", "primary"),
  field("ovrPot", "Overall/Potential", "primary"),
  field("teamNeed", "Team need", "primary"),
  field("range", "Ceiling/floor", "primary"),
  field("currentPick", "Current pick", "secondary"),
  field("recommendation", "Recommendation", "secondary"),
  field("projectedPick", "Projected pick", "secondary"),
  field("fullStats", "Full stats", "hidden"),
  field("contract", "Contract", "hidden"),
  field("draftedFullTable", "Full drafted table", "hidden"),
];

export const protectPlayerFields: DisplayField[] = [
  field("name", "Name", "primary"),
  field("positionAge", "Position/Age", "primary"),
  field("ovrPot", "Overall/Potential", "primary"),
  field("protectionRecommendation", "Protection recommendation", "primary"),
  field("contract", "Contract", "primary"),
  field("coreStats", "Core stats", "secondary"),
  field("fullRatings", "Full ratings", "hidden"),
  field("fullStats", "Full stats", "hidden"),
  field("acquired", "Acquired", "hidden"),
];

export const injuryFields: DisplayField[] = [
  field("teamPlayer", "My injured player", "primary"),
  field("injuryType", "Injury", "primary"),
  field("gamesOut", "Games out", "primary"),
  field("starterImpact", "Starter impact", "primary"),
  field("playingThrough", "Playing through", "secondary"),
  field("ovrDrop", "Ovr Drop", "hidden"),
  field("potDrop", "Pot Drop", "hidden"),
  field("fullStats", "League-wide stats", "hidden"),
];

export const homeFields: DisplayField[] = [
  field("phase", "Phase", "primary"),
  field("nextStep", "Next step", "primary"),
  field("recordRank", "Record/rank", "primary"),
  field("teamStrength", "Team strength", "primary"),
  field("salaryPressure", "Salary pressure", "primary"),
  field("alerts", "Injury/contract alerts", "primary"),
  field("nextGame", "Next game", "primary"),
  field("recentNews", "Recent news", "secondary"),
  field("topPlayer", "Top player", "secondary"),
  field("fullLeaders", "Full leaders", "hidden"),
  field("fullLineup", "Full lineup", "hidden"),
  field("historyArchive", "History archive", "hidden"),
];

export const teamFinanceFields: DisplayField[] = [
  field("payroll", "Payroll", "primary"),
  field("payrollLimit", "Payroll limit", "primary"),
  field("availableSpace", "Available space", "primary"),
  field("nextSeasonPressure", "Next season pressure", "primary"),
  field("releasedContracts", "Released contracts", "secondary"),
  field("ticketPrice", "Ticket price", "hidden"),
  field("attendance", "Attendance", "hidden"),
  field("revenue", "Revenue", "hidden"),
  field("profit", "Profit", "hidden"),
  field("cash", "Cash", "hidden"),
  field("budgetDetail", "Budget detail", "hidden"),
  field("financialHistory", "Financial history", "hidden"),
];

export const standingsTeamFields: DisplayField[] = [
  field("rank", "Rank", "primary"),
  field("record", "Record", "primary"),
  field("gamesBack", "Games back", "primary"),
  field("recentForm", "Recent form", "primary"),
  field("playoffGap", "Playoff gap", "secondary"),
  field("pct", "Winning percentage", "secondary"),
  field("streak", "Streak", "secondary"),
  field("mov", "Point differential", "hidden"),
  field("homeAway", "Home/away split", "hidden"),
  field("divisionConference", "Division/conference split", "hidden"),
  field("tiebreakerDetail", "Tiebreaker detail", "hidden"),
];

export const leaderDefaultStats = ["pts", "trb", "ast", "stl", "blk", "per"] as const;

export const playerStatsDefaultStats = ["pts", "trb", "ast", "fgp", "per"] as const;

export const playerRatingsDefaultFields = ["ovr", "pot", "skills"] as const;

export const teamStatsDefaultStats = ["pts", "oppPts", "mov", "ortg", "drtg", "nrtg"] as const;

// B3 mobile table column budgets: on window.mobile, player-type tables render
// at most 12 default columns and team-type tables at most 10 (including rank
// and name/team columns). Desktop column sets are unchanged.
export const mobilePlayerTableColumnBudget = 12;

export const mobileTeamTableColumnBudget = 10;

// B3: teamStats mobile base columns — record only; gp / OTL / ties / points /
// average age stay desktop-only so the 4 base + 6 default stats fit the
// 10-column team budget.
export const teamStatsMobileBaseColNames = ["#", "Team", "W", "L"] as const;

// B3: draftHistory mobile career-stat subset — with the 7 identity/draft
// columns (Name, Pick, Pos, Team, Age, Ovr, Pot) these 5 stats fit the
// 12-column player budget. The current/peak rating blocks and skills stay
// desktop-only (their tri-"Ovr" labels are ambiguous without superCols).
export const draftHistoryDefaultStats = ["gp", "pts", "trb", "ast", "per"] as const;

export const draftProspectPreviewLimit = 8;

const createMobileDisplayPolicy = (
  id: string,
  fields: readonly DisplayField[],
  maxPrimaryFields: number,
): MobileDisplayPolicy => {
  const primaryFields = fields.filter((fieldInfo) => fieldInfo.tier === "primary");
  const secondaryFields = fields.filter((fieldInfo) => fieldInfo.tier === "secondary");
  const hiddenFields = fields.filter((fieldInfo) => fieldInfo.tier === "hidden");

  return {
    id,
    fields,
    primaryFields,
    secondaryFields,
    hiddenFields,
    primaryFieldKeys: primaryFields.map((fieldInfo) => fieldInfo.key),
    secondaryFieldKeys: secondaryFields.map((fieldInfo) => fieldInfo.key),
    hiddenFieldKeys: hiddenFields.map((fieldInfo) => fieldInfo.key),
    maxPrimaryActions: 1,
    maxPrimaryFields,
  };
};

export const mobileDisplayPolicies = {
  home: createMobileDisplayPolicy("home", homeFields, 7),
  rosterPlayer: createMobileDisplayPolicy("rosterPlayer", rosterPlayerFields, 5),
  marketPlayer: createMobileDisplayPolicy("marketPlayer", marketPlayerFields, 6),
  tradeAsset: createMobileDisplayPolicy("tradeAsset", tradeAssetFields, 5),
  draftProspect: createMobileDisplayPolicy("draftProspect", draftProspectFields, 6),
  standingsTeam: createMobileDisplayPolicy("standingsTeam", standingsTeamFields, 4),
} as const;

export const filterPolicyKeys = <T extends string>(values: T[], policy: readonly string[]) => {
  const policySet = new Set(policy);
  const filtered = values.filter((value) => policySet.has(value));

  return filtered.length > 0 ? filtered : values.slice(0, policy.length);
};

export const getPolicyFieldTier = (
  policy: readonly DisplayField[],
  key: string,
): FieldTier | undefined => policy.find((fieldInfo) => fieldInfo.key === key)?.tier;
